/**
 * fetchWithRetry.ts
 *
 * A fetch() wrapper with a per-attempt timeout (AbortController) and a few retries with a short
 * backoff. Real-world mobile connections -- even over WiFi, even on the SAME local network as
 * the server -- can have brief stalls that cause an individual request to fail or hang, even
 * though the underlying connection is fine moments later: iOS/WebKit in particular suspends a
 * backgrounded tab's JS execution and in-flight network activity fairly aggressively (e.g. the
 * screen auto-locking, or switching apps briefly), which can silently kill or indefinitely
 * delay a `fetch()` that was already in progress.
 *
 * This was confirmed happening in practice (not theoretical): a user's initial load appeared
 * permanently stuck on "Loading GFS manifest...", and nginx's access log showed the request
 * sequence eventually DID complete successfully, just with an unexplained multi-minute gap in
 * the middle where the page had no way to know whether to keep waiting or give up. Without
 * retries, a single such stall among the many fetches fired during startup can sink the whole
 * page to a degraded fallback even though the vast majority of real data was actually available
 * moments later.
 *
 * Usable from both the main thread and the particle physics Web Worker -- fetch/AbortController/
 * setTimeout are all available in both contexts (none of these are DOM-specific APIs).
 */
export async function fetchWithRetry(url: string, attempts = 3, timeoutMs = 20000): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      if (attempt < attempts - 1) {
        // Short, increasing backoff before the next attempt -- not scientifically tuned, just
        // enough to let a transient blip (the device waking back up, a brief WiFi hiccup) clear.
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}
